import React, { useState } from "react";
import {
  XMarkIcon,
  ArrowsUpDownIcon,
  PencilIcon,
  BeakerIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ReportColumn } from "./types";
import { DATA_TYPE_CONFIG } from "./constants";
import { useReportBuilderStore } from "./store";
import { ColumnContextMenu, ColumnContextInfo } from "./ColumnContextMenu";

// ─── Broken reference indicator ───────────────────────────────────────────────

function BrokenRefWarning({ refs }: { refs: string[] }) {
  return (
    <div className="flex items-center gap-1 mt-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-700">
      <ExclamationTriangleIcon className="w-3 h-3 flex-shrink-0" />
      <span>
        Column{refs.length > 1 ? "s" : ""} not found:{" "}
        <span className="font-mono">{refs.join(", ")}</span>
      </span>
    </div>
  );
}

// ─── Column card ──────────────────────────────────────────────────────────────

interface ColumnCardProps {
  col: ReportColumn;
  allColumnIds: Set<string>;
}

export function ColumnCard({ col, allColumnIds }: ColumnCardProps) {
  const {
    removeColumn,
    setActiveColumn,
    activeColumnId,
    config,
    activeSheetId,
  } = useReportBuilderStore();

  const activeSheet =
    config.sheets.find((s) => s.id === activeSheetId) ?? config.sheets[0];
  const isActive = activeColumnId === col.id;
  const isGrouped = activeSheet.groupBy.includes(col.id);
  const sortRule = activeSheet.sortBy.find((s) => s.column === col.id);
  const cfg = DATA_TYPE_CONFIG[col.type];
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);

  const ctxInfo: ColumnContextInfo = {
    displayName: col.displayName,
    dataType: col.type,
    description: col.description,
    sourceTable: col.sourceTable ?? undefined,
    sourceColumn: col.sourceColumn ?? undefined,
    isCustom: col.isCustom,
    formula: col.formula,
    aggregation: col.aggregation ?? null,
    sortDirection: sortRule?.direction ?? null,
    isGrouped,
  };

  // Detect broken formula references
  const brokenRefs: string[] = [];
  if (col.isCustom && col.formula) {
    const refs = [...col.formula.matchAll(/\{([^}]+)\}/g)].map((m) => m[1]);
    for (const ref of refs) {
      const refLower = ref.toLowerCase();
      const found = activeSheet.columns.some(
        (c) =>
          c.id !== col.id &&
          (c.sourceColumn?.toLowerCase() === refLower ||
            c.displayName.toLowerCase() === refLower)
      );
      if (!found) brokenRefs.push(ref);
    }
  }

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: col.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <>
      {ctxMenu && (
        <ColumnContextMenu
          info={ctxInfo}
          x={ctxMenu.x}
          y={ctxMenu.y}
          onClose={() => setCtxMenu(null)}
        />
      )}
      <div
        ref={setNodeRef}
        style={style}
        className={`relative bg-white rounded-xl border-2 p-3 cursor-pointer transition-all select-none ${
          isActive
            ? "border-primary shadow-md shadow-activeColor"
            : "border-gray-200 hover:border-gray-300 shadow-sm"
        } ${brokenRefs.length > 0 ? "border-amber-300" : ""}`}
        onClick={() => setActiveColumn(isActive ? null : col.id)}
        onContextMenu={(e) => {
          e.preventDefault();
          setCtxMenu({ x: e.clientX, y: e.clientY });
        }}
      >
        {/* Drag handle */}
        <div
          {...attributes}
          {...listeners}
          className="absolute top-2 left-2 cursor-grab text-gray-300 hover:text-gray-500 transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <ArrowsUpDownIcon className="w-3.5 h-3.5" />
        </div>

        {/* Remove button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            removeColumn(col.id);
          }}
          className="absolute top-2 right-2 text-gray-300 hover:text-red-400 transition-colors"
          title="Remove column"
        >
          <XMarkIcon className="w-3.5 h-3.5" />
        </button>

        {/* Content */}
        <div className="pl-4 pr-4">
          {/* Header row */}
          <div className="flex items-center gap-1.5 mb-1">
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}
            >
              {col.isCustom ? "ƒ" : cfg.label}
            </span>
            {col.isCustom && (
              <BeakerIcon
                className="w-3 h-3 text-amber-500"
                title="Custom formula"
              />
            )}
          </div>

          {/* Display name */}
          <div className="text-xs font-semibold text-gray-800 leading-snug truncate">
            {col.displayName}
          </div>

          {/* Source info */}
          {!col.isCustom && col.sourceTable && (
            <div className="text-[10px] text-gray-400 mt-0.5 truncate">
              {col.sourceTable}.{col.sourceColumn}
            </div>
          )}

          {/* Formula preview */}
          {col.isCustom && col.formula && (
            <div className="text-[10px] text-gray-500 mt-0.5 font-mono truncate">
              {col.formula}
            </div>
          )}

          {/* Badges row */}
          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
            {col.aggregation && (
              <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-[10px] font-medium border border-blue-200">
                {col.aggregation.toUpperCase()}
              </span>
            )}
            {isGrouped && (
              <span className="px-1.5 py-0.5 rounded bg-purple-50 text-primary text-[10px] font-medium border border-purple-200">
                GROUP BY
              </span>
            )}
            {sortRule && (
              <span className="px-1.5 py-0.5 rounded bg-gray-50 text-gray-600 text-[10px] font-medium border border-gray-200">
                {sortRule.direction === "asc" ? "↑ ASC" : "↓ DESC"}
              </span>
            )}
          </div>

          {/* Broken references */}
          {brokenRefs.length > 0 && <BrokenRefWarning refs={brokenRefs} />}
        </div>

        {/* Active indicator */}
        {isActive && (
          <div className="flex items-center justify-center gap-1 mt-2 pt-2 border-t border-activeColor">
            <PencilIcon className="w-3 h-3 text-primary" />
            <span className="text-[10px] text-primary">Editing</span>
          </div>
        )}
      </div>
    </>
  );
}
